import React, { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  Legend,
} from "recharts";

const TENANTS_Q = gql`
  query DashboardTenants {
    getAllTentants {
      _id
      firstName
      lastName
      email
    }
  }
`;

const SUPPORT_Q = gql`
  query DashboardSupport {
    getAllSupportTickets {
      id
      subject
      status
      priority
      tenant_id
    }
  }
`;

const PRICING_Q = gql`
  query DashboardPricing {
    getAllPricingPlans {
      id
      planName
    }
  }
`;

const TEAM_Q = gql`
  query DashboardTeam {
    getAllTeams {
      _id
      name
    }
  }
`;

const FAQ_Q = gql`
  query DashboardFaqs {
    getAllFaqsQuery {
      id
      question
    }
  }
`;

const CHART_COLORS = ["#0d9488", "#6366f1", "#f59e0b", "#ec4899", "#64748b", "#22c55e"];

function StatCard({ title, value, subtitle, icon: Icon, loading, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        height: "100%",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? { transform: "translateY(-2px)", boxShadow: 6 }
          : undefined,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={72} height={40} sx={{ mt: 1 }} />
            ) : (
              <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800 }}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "common.white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.95,
            }}
          >
            <Icon fontSize="small" />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function DashBoard() {
  const navigate = useNavigate();
  const qOpts = { fetchPolicy: "network-only" };

  const tenants = useQuery(TENANTS_Q, qOpts);
  const support = useQuery(SUPPORT_Q, qOpts);
  const pricing = useQuery(PRICING_Q, qOpts);
  const team = useQuery(TEAM_Q, qOpts);
  const faqs = useQuery(FAQ_Q, qOpts);

  const loading =
    tenants.loading || support.loading || pricing.loading || team.loading || faqs.loading;

  const tenantList = tenants.data?.getAllTentants ?? [];
  const ticketList = support.data?.getAllSupportTickets ?? [];

  const openTickets = useMemo(() => {
    return ticketList.filter((t) => {
      const s = String(t.status || "").toLowerCase();
      return s && !s.includes("close");
    }).length;
  }, [ticketList]);

  const statusSlices = useMemo(() => {
    const map = {};
    ticketList.forEach((t) => {
      const key = t.status?.trim() || "Unknown";
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [ticketList]);

  const recentTickets = useMemo(() => ticketList.slice(0, 6), [ticketList]);
  const recentTenants = useMemo(() => tenantList.slice(0, 5), [tenantList]);

  const hasError =
    tenants.error || support.error || pricing.error || team.error || faqs.error;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Overview
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Live counts from your GraphQL API — tenants, support, catalog, and team.
          </Typography>
        </Box>
        <Button
          variant="contained"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={() => navigate("/billing")}
        >
          Billing insights
        </Button>
      </Stack>

      {hasError && (
        <Typography color="error" sx={{ mb: 2 }}>
          Some metrics failed to load. Check the network or GraphQL endpoint.
        </Typography>
      )}

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2.5}
        sx={{ mb: 3, "& > *": { flex: "1 1 200px", minWidth: { xs: "100%", sm: 200, md: 180 } } }}
      >
        <StatCard
          title="Tenants"
          value={tenantList.length}
          subtitle="Registered accounts"
          icon={ApartmentRoundedIcon}
          loading={tenants.loading}
          onClick={() => navigate("/tenants")}
        />
        <StatCard
          title="Open tickets"
          value={openTickets}
          subtitle={`${ticketList.length} total`}
          icon={SupportAgentRoundedIcon}
          loading={support.loading}
          onClick={() => navigate("/support")}
        />
        <StatCard
          title="Pricing plans"
          value={(pricing.data?.getAllPricingPlans ?? []).length}
          subtitle="Stripe-linked rows"
          icon={SellRoundedIcon}
          loading={pricing.loading}
          onClick={() => navigate("/price")}
        />
        <StatCard
          title="Team"
          value={(team.data?.getAllTeams ?? []).length}
          subtitle="Superadmin users"
          icon={GroupsRoundedIcon}
          loading={team.loading}
          onClick={() => navigate("/team")}
        />
        <StatCard
          title="FAQ entries"
          value={(faqs.data?.getAllFaqsQuery ?? []).length}
          subtitle="Help center"
          icon={QuizRoundedIcon}
          loading={faqs.loading}
          onClick={() => navigate("/faq")}
        />
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <Card sx={{ height: 360 }}>
            <CardContent sx={{ height: "100%" }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Tickets by status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Distribution of all support tickets
              </Typography>
              {support.loading ? (
                <Skeleton variant="rounded" height={260} />
              ) : statusSlices.length === 0 ? (
                <Box
                  sx={{
                    height: 260,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">No tickets yet</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={statusSlices}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={56}
                      outerRadius={88}
                      paddingAngle={2}
                    >
                      {statusSlices.map((entry, index) => (
                        <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <ReTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Recent support</Typography>
                <Button size="small" onClick={() => navigate("/support")}>
                  View all
                </Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {support.loading
                    ? [...Array(4)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={3}>
                            <Skeleton />
                          </TableCell>
                        </TableRow>
                      ))
                    : recentTickets.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/support-viewdetails/${row.id}`)}
                        >
                          <TableCell>{row.subject || "—"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.status || "—"} variant="outlined" />
                          </TableCell>
                          <TableCell>{row.priority || "—"}</TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
              {!support.loading && recentTickets.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  No support tickets to show.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Recent tenants</Typography>
                <Button size="small" onClick={() => navigate("/tenants")}>
                  Manage tenants
                </Button>
              </Stack>
              <Grid container spacing={2}>
                {tenants.loading
                  ? [...Array(3)].map((_, i) => (
                      <Grid item xs={12} md={4} key={i}>
                        <Skeleton variant="rounded" height={72} />
                      </Grid>
                    ))
                  : recentTenants.map((t) => (
                      <Grid item xs={12} md={4} key={t._id}>
                        <Card
                          variant="outlined"
                          onClick={() => navigate(`/tenant-details/${t._id}`)}
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            borderRadius: 2,
                            "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                          }}
                        >
                          <Typography fontWeight={700}>
                            {[t.firstName, t.lastName].filter(Boolean).join(" ") || "Tenant"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {t.email}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
              </Grid>
              {!tenants.loading && recentTenants.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 1 }}>
                  No tenants loaded.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
